import { CircleAlert, Loader } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import AuthCheckingImage from '@/assets/images/auth-checking.png';

export type AuthCheckingScreenProps = {
  failed?: boolean;
};

/**
 * The "Checking authorization" screen, shared by both guards. It lives in the app
 * rather than in `@consigli/oauth` because neither the Tailwind classes nor the
 * translations below exist in that lib: the apps' Tailwind scans only their own
 * `src` plus `libs/shadcn`, and the lib has no i18n. `AuthGuard` takes it as its
 * `loading` prop so the first screen of a login is the designed one too.
 */
export const AuthCheckingScreen: FC<AuthCheckingScreenProps> = ({ failed = false }) => {
  const { t } = useTranslation();

  // min-h-screen, not h-screen: same centring, but a short viewport grows and
  // scrolls instead of clipping the 259px block. `ds-body-font` here because
  // this mounts above RootLayout, so it inherits nothing from the app shell.
  return (
    <div className="ds-body-font flex min-h-screen flex-col items-center justify-center">
      {/* The frame export, not the raw image: the design crops a 1.33 source to
          1.58 inside this box, so the crop is baked into the asset. */}
      <img
        src={AuthCheckingImage}
        alt=""
        width={320}
        height={203}
        className="h-[203px] w-[320px]"
      />
      {/* 56px row = the design's 24px content plus 16px above and below, at the
          design's `heading 4` — 20px/24, weight 500.

          The loader is Lucide `Loader`, which is the mark the design draws:
          eight spokes at 45 degrees, radius 6 to 10. The facade Spinner is a
          border arc — a different icon, and one with no 24px step.

          The failure state has no design of its own. A loader there would say
          work is in progress while the text says it failed, so it swaps for the
          icon the design system already assigns to a negative message —
          Callout maps variant="negative" to Lucide CircleAlert. Same size, so
          the row geometry does not shift. */}
      <div role="status" className="flex h-14 items-center gap-4">
        {failed ? (
          <CircleAlert size={24} className="text-[var(--ds-color-text-negative)]" aria-hidden />
        ) : (
          <Loader size={24} className="animate-spin text-day-neutral-dark" aria-hidden />
        )}
        <span className="text-xl font-medium leading-6 text-day-neutral-dark">
          {failed ? t('auth.unable-to-authenticate') : t('auth.checking-authorization')}
        </span>
      </div>
    </div>
  );
};
